'use client';

import React, {
	CSSProperties,
	PropsWithChildren,
	useEffect,
	useRef,
	useMemo,
} from 'react';
import useResponsiveSpan, { SizeConfig } from '../useResponsiveSpan';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';

interface ContentGridProps extends WithChildren, WithClassName {
	cols: number;
	autoRows?: CSSProperties['gridAutoRows'];
	alignItems?: CSSProperties['alignItems'];
	columnGap?: CSSProperties['columnGap'];
	rowGap?: CSSProperties['rowGap'];
	id?: string;
	maxWidth?: number;
	innerClassName?: string;
}

/**
 * Customizable grid that arranges content into cells spanning a custom number
 * of columns and automatically flows to the next row when columns are filled.
 */
const ContentGrid = ({
	className,
	cols,
	children,
	columnGap,
	rowGap,
	maxWidth,
	alignItems = 'start',
	autoRows = 'max-content',
	innerClassName,
}: ContentGridProps) => {
	const style = useMemo(
		() => ({
			gridTemplateColumns: `repeat(${cols}, 1fr)`,
			gridAutoRows: autoRows,
			columnGap,
			rowGap,
			alignItems,
			maxWidth,
		}),
		[cols, autoRows, columnGap, rowGap, alignItems, maxWidth]
	);

	return (
		<div className={cj('flex w-full justify-center', className)}>
			<div className={cj('inline-grid w-full', innerClassName)} style={style}>
				{children}
			</div>
		</div>
	);
};

interface GridCellProps extends WithChildren, WithClassName {
	span: number;
}

/**
 * Cell spanning a custom amount of rows of the parent `ContentGrid`.
 */
const GridCell = ({
	className,
	span,
	children,
	ref,
}: WithRef<GridCellProps, HTMLDivElement>) => {
	const style = useMemo(
		() => ({ gridColumn: `span ${span} / span ${span}` }),
		[span]
	);

	return (
		<div className={cj('shrink-0', className)} style={style} ref={ref}>
			{children}
		</div>
	);
};

interface ResponsiveCellProps extends WithChildren, WithClassName {
	sizeConfig: SizeConfig;
}

/**
 * Cell that lets you customize its span based on screen size.
 */
const ResponsiveCell = ({
	className,
	sizeConfig,
	children,
	ref,
}: WithRef<ResponsiveCellProps, HTMLDivElement>) => {
	const currentSpanConfig = useResponsiveSpan(sizeConfig) ?? 1;

	return (
		<GridCell
			span={currentSpanConfig}
			className={cm('flex flex-col', className)}
			ref={ref}
		>
			{children}
		</GridCell>
	);
};

const ContentWrapper = (
	props: PropsWithChildren<{
		className?: string;
		style?: CSSProperties;
		fullWidth?: boolean;
		wrap?: boolean;
	}>
) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!props.wrap) return;

		// handler on window resize for removing left border (divider) on first element of each row
		// in a flex wrap container
		const handleResize = () => {
			const childElements = Array.from(wrapperRef.current?.children ?? []);

			// first child will always be the left most element
			const leftMostPosition =
				childElements[0]?.getBoundingClientRect().left ?? 0;

			childElements.forEach((child) => {
				const childElementLeft = child.getBoundingClientRect().left;

				if (childElementLeft === leftMostPosition) {
					child.classList.add('border-l-0!');
				} else {
					child.classList.remove('border-l-0!');
				}
			});
		};

		// set resize handler
		window.addEventListener('resize', handleResize);

		// call handler right away so state gets updated with initial window size
		handleResize();

		// sometimes on page load the resize handler doesn't do anything right away
		setTimeout(handleResize, 1000);

		// clean up
		return () => window.removeEventListener('resize', handleResize);
	}, [props.wrap]);

	return (
		<div
			className={cm(
				`h-full overflow-hidden rounded-xs border border-stroke-divider bg-container-bg p-4`,
				props.fullWidth && 'w-full',
				props.wrap && 'flex flex-wrap',
				props.className
			)}
			ref={wrapperRef}
		>
			{props.children}
		</div>
	);
};

ContentGrid.ContentWrapper = ContentWrapper;
ContentGrid.Cell = GridCell;
ContentGrid.ResponsiveCell = ResponsiveCell;
export default ContentGrid;
